import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { PROFILE_PUBLIC_COLUMNS } from "@/lib/profileColumns";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

const getStoredBrowserGeo = (): { browser_lat?: number; browser_lon?: number } => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem("cg_geo_coords_v1");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed?.latitude === "number" && typeof parsed?.longitude === "number") return { browser_lat: parsed.latitude, browser_lon: parsed.longitude };
  } catch { /* ignore */ }
  return {};
};

interface AuthContextType {
  session: Session | null; user: User | null; role: AppRole | null;
  profile: Database["public"]["Tables"]["profiles"]["Row"] | null; loading: boolean;
  subscriptionActive: boolean; subscriptionEnd: string | null;
  checkSubscription: () => Promise<void>; refreshProfile: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role: AppRole, proCategory?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);

const withTimeout = async <T,>(promise: PromiseLike<T>, ms = 7000): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve(promise),
      new Promise<T>((_, reject) => { timer = setTimeout(() => reject(new Error("Request timeout")), ms); }),
    ]);
  } finally { if (timer) clearTimeout(timer); }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null); const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null); const [profile, setProfile] = useState<Database["public"]["Tables"]["profiles"]["Row"] | null>(null);
  const [loading, setLoading] = useState(true); const [subscriptionActive, setSubscriptionActive] = useState(false); const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);

  const ensureProfileExists = async (userId: string, userEmail?: string, userMeta?: Record<string, any>) => {
    const { data: existingProfile } = await withTimeout(supabase.from("profiles").select(PROFILE_PUBLIC_COLUMNS as any).eq("user_id", userId).maybeSingle<any>());
    if (existingProfile) return existingProfile;
    const fullName = userMeta?.full_name || userMeta?.name || userEmail?.split("@")[0] || "";
    const { data: newProfile, error } = await withTimeout(supabase.from("profiles").insert({ user_id: userId, full_name: fullName, pro_category: userMeta?.pro_category || null }).select(PROFILE_PUBLIC_COLUMNS as any).single<any>());
    if (error) { console.warn("Fallback profile unavailable:", error.message); return null; }
    return newProfile;
  };

  const ensureRoleExists = async (userId: string, userMeta?: Record<string, any>) => {
    const { data: existingRoles } = await withTimeout(supabase.from("user_roles").select("role").eq("user_id", userId));
    if (existingRoles?.length) {
      for (const r of ["admin", "pro", "parent"] as AppRole[]) if (existingRoles.some(row => row.role === r)) return r;
      return existingRoles[0].role;
    }
    const selectedRole = (userMeta?.role as AppRole) || "parent";
    const { error } = await withTimeout(supabase.from("user_roles").insert({ user_id: userId, role: selectedRole }));
    if (error) console.warn("Fallback role unavailable:", error.message);
    return selectedRole;
  };

  const fetchUserData = useCallback(async (currentUser: User) => {
    try {
      const results = await Promise.allSettled([ensureRoleExists(currentUser.id, currentUser.user_metadata), ensureProfileExists(currentUser.id, currentUser.email, currentUser.user_metadata)]);
      const r = results[0].status === "fulfilled" ? results[0].value : ((currentUser.user_metadata?.role as AppRole) || "parent");
      const p = results[1].status === "fulfilled" ? results[1].value : null;
      setRole(r ?? "parent"); setProfile(p ?? null);
    } catch (err) { console.warn("Non-blocking user data load error:", err); setRole((currentUser.user_metadata?.role as AppRole) || "parent"); }
  }, []);

  const refreshProfile = useCallback(async () => { if (user) await fetchUserData(user); }, [user, fetchUserData]);

  const checkSubscription = async () => { try { const { data, error } = await withTimeout(supabase.functions.invoke("check-subscription"), 5000); if (!error && data) { setSubscriptionActive(data.subscribed ?? false); setSubscriptionEnd(data.subscription_end ?? null); } } catch { /* never block auth */ } };

  useEffect(() => {
    let lastTrackAt = 0;
    const trackSession = (evt: string) => { const now = Date.now(); if (now-lastTrackAt<30000) return; lastTrackAt=now; supabase.functions.invoke("track-pro-login", { body:{event_type:"login",trigger:evt,...getStoredBrowserGeo()} }).catch(()=>{}); };
    const { data:{subscription} } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession); setUser(nextSession?.user ?? null);
      if (nextSession?.user) {
        // A valid Supabase session is sufficient to leave the login loading state.
        setLoading(false);
        if (["SIGNED_IN","TOKEN_REFRESHED","INITIAL_SESSION"].includes(event)) setTimeout(()=>trackSession(event),800);
        setTimeout(()=>{ void fetchUserData(nextSession.user); },0);
      } else { setRole(null); setProfile(null); setLoading(false); }
    });
    withTimeout(supabase.auth.getSession(), 7000).then(({data:{session: initial}}) => {
      setSession(initial); setUser(initial?.user ?? null); setLoading(false);
      if (initial?.user) { void fetchUserData(initial.user); setTimeout(()=>void checkSubscription(),500); }
    }).catch(err => { console.warn("Initial session load failed:",err); setLoading(false); });
    const interval=setInterval(()=>{ supabase.auth.getSession().then(({data:{session:s}})=>{if(s) void checkSubscription();}).catch(()=>{}); },60000);
    return()=>{subscription.unsubscribe();clearInterval(interval);};
  }, [fetchUserData]);

  const signUp = async (email:string,password:string,fullName:string,selectedRole:AppRole,proCategory?:string) => {
    const metadata:Record<string,string>={full_name:fullName,role:selectedRole}; if(selectedRole==="pro"&&proCategory)metadata.pro_category=proCategory;
    try {
      const {data,error}=await withTimeout(supabase.auth.signUp({email,password,options:{data:metadata,emailRedirectTo:"https://clickandgard.fr/email-valide"}}),12000);
      if(error)return{error}; if(data.user?.identities?.length===0)return{error:new Error("Un compte existe déjà avec cet email. Essayez de vous connecter.")};
      if(data.user) supabase.functions.invoke("notify-admin-new-user",{body:{record:{id:data.user.id,email,raw_user_meta_data:metadata,created_at:new Date().toISOString()}}}).catch(()=>{});
      return{error:null};
    } catch(e){return{error:e instanceof Error?e:new Error("Connexion au serveur impossible. Réessayez.")};}
  };

  const signIn = async (email:string,password:string) => {
    try {
      const {data,error}=await withTimeout(supabase.auth.signInWithPassword({email,password}),12000); if(error)return{error:error as Error};
      // Login success must not depend on optional profile/tracking requests.
      if(data.session){setSession(data.session);setUser(data.user);setLoading(false);}
      if(data.user){
        void withTimeout(supabase.from("profiles").select("suspended_at, suspension_reason").eq("user_id",data.user.id).maybeSingle(),5000).then(async({data:prof})=>{if(prof?.suspended_at){await supabase.auth.signOut();setSession(null);setUser(null);setRole(null);}}).catch(()=>{});
        supabase.functions.invoke("track-pro-login",{body:{event_type:"login",...getStoredBrowserGeo()}}).catch(()=>{});
        void fetchUserData(data.user);
      }
      return{error:null};
    } catch(e){return{error:e instanceof Error?e:new Error("Le serveur ne répond pas. Vérifiez votre connexion et réessayez.")};}
  };

  useEffect(()=>{const h=()=>{if(sessionStorage.getItem("clickandgard_no_persist")==="true"){supabase.auth.signOut();sessionStorage.removeItem("clickandgard_no_persist");}};window.addEventListener("beforeunload",h);return()=>window.removeEventListener("beforeunload",h);},[]);
  const signOut=async()=>{sessionStorage.removeItem("clickandgard_no_persist");await supabase.auth.signOut();setSubscriptionActive(false);setSubscriptionEnd(null);setRole(null);setProfile(null);};
  return <AuthContext.Provider value={{session,user,role,profile,loading,subscriptionActive,subscriptionEnd,checkSubscription,refreshProfile,signUp,signIn,signOut}}>{children}</AuthContext.Provider>;
};
export const useAuth=()=>{const ctx=useContext(AuthContext);if(!ctx)throw new Error("useAuth must be used within AuthProvider");return ctx;};
